import { ChevronDown, ChevronUp, Languages, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  CALLER_LANGUAGES_DEEPGRAM_HELP,
  CALLER_LANGUAGES_DEEPGRAM_NONE,
  CALLER_LANGUAGES_HELP,
  CALLER_LANGUAGES_NONE,
  callerLanguageName,
} from "@/constants/callerLanguages";
import { deepgramRowNote, type Transcriber } from "@/constants/transcribers";
import { CallerLanguagePicker } from "./CallerLanguagePicker";

interface CallerLanguagesProps {
  value: string[];
  onChange: (value: string[]) => void;
  /** Who hears the caller, on a text model whose Transcriber dropdown shows (`showSupport`). */
  transcriber?: Transcriber;
  showSupport?: boolean;
}

/**
 * The languages the agent's callers speak, most spoken first (`config.settings.callerLanguages`, saved on Publish;
 * owner, 2026-10-29): added at the end from a searchable dialog, several at once (owner, 2026-11-02), moved up or
 * down, removed. At the top of Voice Settings (owner, 2026-10-31). On a text model heard by Deepgram it says what
 * Deepgram can hear: a language it cannot hear, or not with the ones listed, cannot be added, and a listed one it
 * cannot hear says so (its calls use Gemini).
 */
export function CallerLanguages({ value, onChange, transcriber, showSupport = false }: CallerLanguagesProps) {
  const deepgram = showSupport && transcriber === "deepgram";

  const move = (index: number, by: -1 | 1) => {
    const next = [...value];
    next.splice(index + by, 0, ...next.splice(index, 1));
    onChange(next);
  };

  return (
    <div className="pb-4 border-b space-y-3">
      <Label id="caller-languages-label" className="text-sm font-medium flex items-center gap-2">
        <Languages className="w-4 h-4 text-primary" />
        Caller Languages
      </Label>
      <p className="text-xs text-muted-foreground">{deepgram ? CALLER_LANGUAGES_DEEPGRAM_HELP : CALLER_LANGUAGES_HELP}</p>
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">{deepgram ? CALLER_LANGUAGES_DEEPGRAM_NONE : CALLER_LANGUAGES_NONE}</p>
      ) : (
        <ol className="space-y-1.5" aria-labelledby="caller-languages-label">
          {value.map((code, index) => {
            const name = callerLanguageName(code);
            const note = deepgram ? deepgramRowNote(value, code) : null;
            return (
              <li
                key={code}
                className="rounded-md border py-1 pl-3 pr-1"
                aria-describedby={note ? `caller-language-note-${code}` : undefined}
              >
                <div className="flex items-center gap-2">
                  <span className="w-4 shrink-0 text-xs text-muted-foreground tabular-nums">{index + 1}</span>
                  <span className="min-w-0 flex-1 truncate text-sm">
                    {name} <span className="text-xs text-muted-foreground">{code}</span>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Move ${name} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  >
                    <ChevronUp className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    aria-label={`Move ${name} down`}
                    disabled={index === value.length - 1}
                    onClick={() => move(index, 1)}
                  >
                    <ChevronDown className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    aria-label={`Remove ${name}`}
                    onClick={() => onChange(value.filter((_, at) => at !== index))}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                {note && (
                  <p id={`caller-language-note-${code}`} className="pb-1 pl-6 pr-2 text-xs text-destructive">
                    {note}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
      <CallerLanguagePicker listed={value} deepgram={deepgram} onAdd={(codes) => onChange([...value, ...codes])} />
    </div>
  );
}
